export type CategoryLevel = "lvl0" | "lvl1" | "lvl2" | "lvl3" | "lvl4";

export default function createUsableCategoryFacet(
  categorySearchLevel: CategoryLevel = "lvl0",
  facets: Record<string, Record<string, number>> | undefined,
  sortAlphanumerically?: boolean,
): CategoryFacetType | null {
  // const categoryUrls = facets?.category_url || {};
  const rawFacets = {
    lvl0: {},
    lvl1: facets?.["category.lvl1"] || {},
    lvl2: facets?.["category.lvl2"] || {},
    lvl3: facets?.["category.lvl3"] || {},
    lvl4: facets?.["category.lvl4"] || {},
  };
  const categoryFacetTree = recursivelyGenerateCategoryTree(rawFacets, categorySearchLevel, sortAlphanumerically);
  return categoryFacetTree;
}

export function getNextCategorySearchLevel(categorySearchLevel: CategoryLevel | undefined): CategoryLevel | null {
  if (!categorySearchLevel) {
    categorySearchLevel = "lvl0";
  }
  switch (categorySearchLevel) {
    case "lvl0":
      return "lvl1";
    case "lvl1":
      return "lvl2";
    case "lvl2":
      return "lvl3";
    case "lvl3":
      return "lvl4";
    case "lvl4":
      return null;
  }
}

function recursivelyGenerateCategoryTree(
  rawFacets: {
    lvl0: Record<string, number>;
    lvl1: Record<string, number>;
    lvl2: Record<string, number>;
    lvl3: Record<string, number>;
    lvl4: Record<string, number>;
  },
  currentLevel?: CategoryLevel,
  sortAlphanumerically?: boolean,
  categoryPropertyName?: string,
  count?: number,
): CategoryFacetType | null {
  if (!categoryPropertyName) {
    categoryPropertyName = "Dental Supplies";
  }
  const categoryNameFriendly = categoryPropertyName ? categoryPropertyName.split(" > ").pop() : categoryPropertyName;
  const returnVal: CategoryFacetType = {
    name: categoryNameFriendly ? categoryNameFriendly : "",
    count: count ? count : 0,
    nameForFaceting: categoryPropertyName ? categoryPropertyName : "",
    children: [],
  };
  const childLevelName: CategoryLevel | null = getNextCategorySearchLevel(currentLevel);
  if (childLevelName) {
    let entries;
    if (sortAlphanumerically) {
      // We need to manually sort the categories in the code since we lose sort ordering when using Object.entries()
      entries = Object.entries(rawFacets[childLevelName]).sort((a, b) => a[0].localeCompare(b[0])); // Alpha (asc) sort on name [0]
    } else {
      entries = Object.entries(rawFacets[childLevelName]).sort((a, b) => b[1] - a[1]); // Numeric (desc) sort on count [1]
    }
    entries.forEach(([categoryName, count]) => {
      if (categoryName.startsWith(categoryPropertyName + " > ")) {
        const childCategory = recursivelyGenerateCategoryTree(
          rawFacets,
          childLevelName,
          sortAlphanumerically,
          categoryName,
          count,
        );
        if (childCategory) {
          returnVal.children.push(childCategory);
        }
      }
    });
    return returnVal;
  } else {
    return null;
  }
}

export type CategoryFacetType = {
  name: string;
  count: number;
  nameForFaceting: string;
  // url: string;
  children: CategoryFacetType[];
};
